/**
 * POSShiftOpenDialog — модал відкриття зміни.
 *
 * Показуємо коли useCurrentShift повертає null (нема відкритої зміни).
 * Викликає POST /retail/cash-shift-open + /post.
 */
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { retailCashShiftOpens } from '@/api/retailCashShiftOpen';
import type { PosWorkstation } from '@/api/retailPosWorkstations';

interface Props {
  workstation: PosWorkstation;
  onOpened: () => void;
  onClose: () => void;
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function POSShiftOpenDialog({ workstation, onOpened, onClose }: Props) {
  const qc = useQueryClient();
  const [openingCash, setOpeningCash] = useState('0');
  const [error, setError] = useState<string | null>(null);

  const createdId = useRef<string | null>(null);
  const submit = useMutation({
    mutationFn: async () => {
      // ч.1343: якщо проведення відмовило, документ уже створено — повторне «провести»
      // оновлює ЙОГО (з виправленнями у вікні), а не створює другий такий самий.
      const doc = createdId.current
        ? await retailCashShiftOpens.update(createdId.current, { opening_cash_amount: openingCash })
        : await retailCashShiftOpens.create({
          organization_id: workstation.organization_id,
          pos_workstation_id: workstation.id,
          opening_cash_amount: openingCash,
        });
      createdId.current = doc.id;
      // Після створення — провести
      await retailCashShiftOpens.post(doc.id);
      return doc;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pos-current-shift'] });
      onOpened();
    },
    onError: (e) => setError(extractError(e)),
  });

  return (
    <div className="pos-modal-overlay">
      <div className="pos-modal">
        <div className="pos-modal__header">
          <span>📋 Відкриття зміни</span>
        </div>
        <div className="pos-modal__body">
          <p style={{ marginTop: 0 }}>
            POS: <strong>{workstation.name} — {workstation.code}</strong>
          </p>
          <div className="pos-modal__field">
            <label>Розмін у касі (грн)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              autoFocus
            />
          </div>
          {error && (
            <div style={{ color: 'var(--danger)', padding: '0.5rem 0' }}>
              {error}
            </div>
          )}
        </div>
        <div className="pos-modal__footer">
          <button className="pos-modal__btn-secondary" onClick={onClose}>
            Скасувати
          </button>
          <button
            className="pos-modal__btn-primary"
            onClick={() => submit.mutate()}
            disabled={submit.isPending}
          >
            {submit.isPending ? 'Відкриваю...' : 'Відкрити зміну'}
          </button>
        </div>
      </div>
    </div>
  );
}
